import GoogleMapsApiLoader from 'google-maps-api-loader';

const PLACE_FIELDS = ['name', 'geometry', 'utc_offset_minutes'];

// Place search and local date/time of the sky, shared by the star map editors. The editor provides
// `selectedOptions` with date, time and location, a `locationInput` ref, and calls initPlaces() when mounted.
export default {
    computed: {
        // One picker edits both stored values: date.value (YYYY-MM-DD) and time (HH:mm, local at the location).
        dateTime: {
            get() {
                const day = this.$dayjs(this.selectedOptions.date.value).format('YYYY-MM-DD');
                return this.$dayjs(`${day}T${this.selectedOptions.time || '00:00'}`).toDate();
            },
            set(value) {
                if (!value) return;
                this.selectedOptions.date.value = this.$dayjs(value).format('YYYY-MM-DD');
                this.selectedOptions.time = this.$dayjs(value).format('HH:mm');
            },
        },
        // UTC timestamp of the chosen local date and time at the selected location.
        skyTime() {
            const day = this.$dayjs(this.selectedOptions.date.value).format('YYYY-MM-DD');
            const time = this.selectedOptions.time || '00:00';
            const { utcOffset } = this.selectedOptions.location;
            if (utcOffset === null) return this.$dayjs(`${day}T${time}`).valueOf();
            return Date.parse(`${day}T${time}:00Z`) - utcOffset * 60000;
        },
    },
    methods: {
        async initPlaces() {
            // GMap normally loads the API; the star map has no GMap, so load it the same way.
            if (!this.$GMaps.google) {
                this.$GMaps.loaded = true;
                this.$GMaps.google = GoogleMapsApiLoader({ apiKey: this.$GMaps.apiKey, language: 'hu' });
            }
            try {
                this.google = await this.$GMaps.google;
                await this.google.maps.importLibrary('places');
                const autocomplete = new this.google.maps.places.Autocomplete(this.$refs.locationInput, {
                    fields: PLACE_FIELDS,
                });
                autocomplete.addListener('place_changed', () => this.setLocation(autocomplete.getPlace()));
            } catch (error) {
                console.error('Error loading Google Places:', error);
            }
        },
        setLocation(place) {
            // Enter without picking a suggestion only gives the typed text.
            if (!place.geometry) {
                this.findFirstPlace(place.name);
                return;
            }
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            const offset = place.utc_offset_minutes;
            this.$refs.locationInput.blur(); // hides the keyboard and lets the preview scroll into view
            this.selectedOptions.location = {
                name: place.name,
                lat,
                lng,
                // shortcut: Places only knows today's UTC offset, so outside the browser's time zone a DST change
                // between today and the chosen date shifts the sky by an hour; use a time zone lookup if that matters.
                utcOffset: offset === -new Date().getTimezoneOffset() ? null : offset ?? Math.round(lng / 15) * 60,
            };
        },
        findFirstPlace(input) {
            if (!input || !this.google) return;
            const { places } = this.google.maps;
            new places.AutocompleteService().getPlacePredictions({ input }, (predictions, status) => {
                if (status !== places.PlacesServiceStatus.OK || !predictions.length) return;
                new places.PlacesService(document.createElement('div')).getDetails(
                    { placeId: predictions[0].place_id, fields: PLACE_FIELDS },
                    (place, detailStatus) => {
                        if (detailStatus === places.PlacesServiceStatus.OK && place.geometry) this.setLocation(place);
                    }
                );
            });
        },
    },
};
